import React, { useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Camera, Upload, Loader2, FileText, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

export default function ReceiptUploader({ kind, itemId, receipts = [], onChange }) {
  const [busy, setBusy] = useState(false);

  const upload = useCallback(async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/${kind}/${itemId}/receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receipt attached");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }, [kind, itemId, onChange]);

  const remove = useCallback(async (rid) => {
    try {
      await api.delete(`/${kind}/${itemId}/receipt/${rid}`);
      toast.success("Receipt removed");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [kind, itemId, onChange]);

  return (
    <div data-testid={`receipts-${kind}-${itemId}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <label className="pill-btn border border-hearth-line bg-hearth-paper hover:bg-hearth-stone text-hearth-ink inline-flex items-center gap-1.5 cursor-pointer"
               data-testid={`receipt-camera-${kind}-${itemId}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />} Take photo
          <input
            type="file"
            hidden
            accept="image/*"
            capture="environment"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
          />
        </label>
        <label className="pill-btn border border-hearth-line bg-hearth-paper hover:bg-hearth-stone text-hearth-ink inline-flex items-center gap-1.5 cursor-pointer"
               data-testid={`receipt-upload-${kind}-${itemId}`}>
          <Upload size={13} /> Upload file
          <input
            type="file"
            hidden
            accept="image/*,application/pdf"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
          />
        </label>
        {receipts.length === 0 && <span className="caption">no receipts yet</span>}
      </div>
      {receipts.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {receipts.map((r) => (
            <div key={r.id} className="inline-flex items-center gap-1.5 chip bg-hearth-sage/12 text-hearth-sageDark" data-testid={`receipt-chip-${r.id}`}>
              <FileText size={12} />
              <a href={`${API}/${kind}/${itemId}/receipt/${r.id}`} target="_blank" rel="noreferrer"
                 className="hover:underline max-w-[160px] truncate" title={r.filename}>
                {r.filename || "receipt"}
              </a>
              <a href={`${API}/${kind}/${itemId}/receipt/${r.id}`} target="_blank" rel="noreferrer" className="opacity-70 hover:opacity-100">
                <Download size={11} />
              </a>
              <button onClick={() => remove(r.id)} data-testid={`receipt-delete-${r.id}`}
                      className="opacity-70 hover:opacity-100 text-hearth-coral">
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
